<script setup lang="ts">
import type { Color } from "@kaleidoscope-solutions/ks-design-system"

import {
  DialogSize,
  KButton,
  KConfirmButton,
  KConfirmDialog
} from "@kaleidoscope-solutions/ks-design-system"
import { ref } from "vue"
import { closeDialog } from "vue3-promise-dialog"

const props = withDefaults(defineProps<DialogProps>(), {
  size: DialogSize.medium,
  okButton: "OK",
  cancelButton: "Cancel",
  cancelButtonColor: "violet",
  cancelTooltip: "Hold to cancel",
  okButtonColor: "violet",
  holdToCancel: false,
  holdToConfirm: true,
  confirmTooltip: "Hold to confirm",
  confirmDialog: false,
  hideCancel: false,
})

const emit = defineEmits<Emits>()

export interface DialogProps {
  open: boolean
  title: string
  testId?: string
  size?: DialogSize
  okButton?: string
  cancelButton?: string
  cancelButtonColor?: Color
  holdToCancel?: boolean
  cancelTooltip?: string
  holdToConfirm?: boolean
  confirmTooltip?: string
  okButtonColor?: string
  body: string | string[]
  imagePath: string
  confirmDialog?: boolean
  hideCancel?: boolean
}

interface Emits {
  (event: "cancel"): void
  (event: "close"): void
}

const body = Array.isArray(props.body) ? props.body : [props.body]

function handleCancel() {
  emit("cancel")
  closeDialog(false)
}

function handleConfirm() {
  closeDialog(true)
  emit("close")
}

const confirmInteractedOnce = ref(false)
const cancelInteractedOnce = ref(false)
</script>

<template>
  <KConfirmDialog
    :open="open"
    :size="size"
  >
    <template #default>
      <h2 class="mb-4 font-vastago text-2xl font-bold">
        {{ title }}
      </h2>
      <div class="flex h-full items-center">
        <div class="max-w-72 pr-9">
          <img
            :src="imagePath"
            class="aspect-auto max-w-48"
            alt=""
          >
        </div>

        <div class="flex flex-col gap-4">
          <template
            v-for="text in body"
            :key="text"
          >
            <div v-sanitize-html="text" />
          </template>
        </div>
      </div>
      <div class="flex w-full items-center justify-end gap-4">
        <template v-if="!hideCancel">
          <KConfirmButton
            v-if="holdToCancel"
            v-tooltip="cancelInteractedOnce ? cancelTooltip : null"
            variety="secondary"
            :color="cancelButtonColor"
            data-test="dialog-btn-cancel"
            @mousedown="cancelInteractedOnce = true"
            @keydown.enter="cancelInteractedOnce = true"
            @confirmed="handleCancel"
          >
            {{ cancelButton }}
          </KConfirmButton>
          <KButton
            v-else
            variety="secondary"
            :color="cancelButtonColor"
            data-test="dialog-btn-cancel"
            @click="handleCancel"
          >
            {{ cancelButton }}
          </KButton>
        </template>

        <template v-if="confirmDialog">
          <KConfirmButton
            v-if="holdToConfirm"
            v-tooltip="confirmInteractedOnce ? confirmTooltip : null"
            variety="primary"
            :color="okButtonColor"
            data-test="dialog-btn-ok"
            @mousedown="confirmInteractedOnce = true"
            @keydown.enter="confirmInteractedOnce = true"
            @confirmed="handleConfirm"
          >
            {{ okButton }}
          </KConfirmButton>
          <KButton
            v-else
            variety="primary"
            :color="okButtonColor"
            data-test="dialog-btn-ok"
            @click="handleConfirm"
            @keydown.enter="handleConfirm"
          >
            {{ okButton }}
          </KButton>
        </template>
        <template v-else>
          <KButton
            :color="okButtonColor"
            data-test="dialog-btn-ok"
            @click="handleConfirm"
          >
            {{ okButton }}
          </KButton>
        </template>
      </div>
    </template>
  </KConfirmDialog>
</template>
